/**
 * Simple trading strategy implementation for testing
 * Uses moving averages crossover
 */
import { HistoricalPrice, BacktestSignal, BacktestOptions } from '../types.js';
import { calculateSMA } from '../utils.js';

/**
 * Generate signals based on SMA crossover strategy
 * - Buy when short-term SMA crosses above long-term SMA
 * - Sell when short-term SMA crosses below long-term SMA
 */
export function smaStrategy(
  historicalData: HistoricalPrice[],
  currentDate: string,
  options: BacktestOptions,
  shortPeriod: number = 20,
  longPeriod: number = 50
): BacktestSignal[] {
  const signals: BacktestSignal[] = [];
  
  // Group data by symbol
  const symbolData: Record<string, HistoricalPrice[]> = {};
  for (const dataPoint of historicalData) {
    if (!symbolData[dataPoint.symbol]) {
      symbolData[dataPoint.symbol] = [];
    }
    symbolData[dataPoint.symbol].push(dataPoint);
  }
  
  // Process each symbol
  for (const symbol of options.symbols) {
    const data = symbolData[symbol];
    if (!data || data.length < longPeriod + 2) continue;
    
    // Sort data chronologically
    data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    // Find data for the current date and previous date
    const today = data.filter(d => d.date.startsWith(currentDate));
    if (today.length === 0) continue;
    
    // Get data up to current day
    const index = data.findIndex(d => d.date === today[0].date);
    if (index < longPeriod + 1) continue;
    
    const dataUpToYesterday = data.slice(0, index);
    const dataUpToToday = data.slice(0, index + 1);
    
    // Extract closing prices
    const closePricesUpToYesterday = dataUpToYesterday.map(d => d.close);
    const closePricesUpToToday = dataUpToToday.map(d => d.close);
    
    // Calculate SMAs for yesterday and today
    const shortSMAYesterday = calculateSMA(closePricesUpToYesterday, shortPeriod);
    const longSMAYesterday = calculateSMA(closePricesUpToYesterday, longPeriod);
    
    const shortSMAToday = calculateSMA(closePricesUpToToday, shortPeriod);
    const longSMAToday = calculateSMA(closePricesUpToToday, longPeriod);
    
    // Skip if any SMA is null
    if (shortSMAYesterday === null || longSMAYesterday === null || 
        shortSMAToday === null || longSMAToday === null) {
      continue;
    }
    
    // Check for crossover
    const crossedAbove = shortSMAYesterday <= longSMAYesterday && shortSMAToday > longSMAToday;
    const crossedBelow = shortSMAYesterday >= longSMAYesterday && shortSMAToday < longSMAToday;
    
    // Generate signal on crossover
    if (crossedAbove) {
      signals.push({
        symbol,
        direction: 'long',
        strength: 1.0,
        price: today[0].close,
        timestamp: today[0].date,
        modelType: 'sma_crossover',
        reason: `${shortPeriod} SMA crossed above ${longPeriod} SMA`
      });
    } else if (crossedBelow) {
      // Generate exit signal if we're going short
      signals.push({
        symbol,
        direction: 'exit',
        strength: 1.0,
        price: today[0].close,
        timestamp: today[0].date,
        modelType: 'sma_crossover',
        reason: `${shortPeriod} SMA crossed below ${longPeriod} SMA`
      });
      
      // Optionally generate short signal
      if (options.symbolSettings.some(s => s.symbol === symbol && s.stopLoss)) {
        signals.push({
          symbol,
          direction: 'short',
          strength: 0.8, // Lower strength for short signals
          price: today[0].close,
          timestamp: today[0].date,
          modelType: 'sma_crossover',
          reason: `${shortPeriod} SMA crossed below ${longPeriod} SMA`
        });
      }
    }
  }
  
  return signals;
}

/**
 * Mean reversion strategy - buy on oversold (RSI < 30), sell on overbought (RSI > 70)
 * This is a placeholder implementation - in a real system, RSI would be calculated properly
 */
export function meanReversionStrategy(
  historicalData: HistoricalPrice[],
  currentDate: string,
  options: BacktestOptions
): BacktestSignal[] {
  // Simplified implementation for testing purposes
  const signals: BacktestSignal[] = [];
  
  // Group data by symbol
  const symbolData: Record<string, HistoricalPrice[]> = {};
  for (const dataPoint of historicalData) {
    if (!symbolData[dataPoint.symbol]) {
      symbolData[dataPoint.symbol] = [];
    }
    symbolData[dataPoint.symbol].push(dataPoint);
  }
  
  // Process each symbol
  for (const symbol of options.symbols) {
    const data = symbolData[symbol];
    if (!data || data.length < 20) continue;
    
    // Sort data chronologically
    data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    // Find data for the current date
    const today = data.filter(d => d.date.startsWith(currentDate));
    if (today.length === 0) continue;
    
    // Simple mean reversion signal based on price relative to recent days
    const index = data.findIndex(d => d.date === today[0].date);
    if (index < 10) continue;
    
    const recentPrices = data.slice(index - 10, index).map(d => d.close);
    const avgPrice = recentPrices.reduce((sum, price) => sum + price, 0) / recentPrices.length;
    
    const currentPrice = today[0].close;
    const percentFromAvg = (currentPrice - avgPrice) / avgPrice * 100;
    
    // Generate signals based on deviation from mean
    if (percentFromAvg < -5) {
      // Price is 5% below recent average - buy signal
      signals.push({
        symbol,
        direction: 'long',
        strength: Math.min(1.0, Math.abs(percentFromAvg) / 10), // Stronger signal for larger deviations
        price: currentPrice,
        timestamp: today[0].date,
        modelType: 'mean_reversion',
        reason: `Price ${percentFromAvg.toFixed(2)}% below 10-day average`
      });
    } else if (percentFromAvg > 5) {
      // Price is 5% above recent average - sell signal
      signals.push({
        symbol,
        direction: 'exit',
        strength: Math.min(1.0, Math.abs(percentFromAvg) / 10),
        price: currentPrice,
        timestamp: today[0].date,
        modelType: 'mean_reversion',
        reason: `Price ${percentFromAvg.toFixed(2)}% above 10-day average`
      });
    }
  }
  
  return signals;
}